/* An editorial example gallery only: it does not select or configure a product. */
(() => {
  'use strict';
  if (customElements.get('ss-original-mix-story')) return;

  class SSOriginalMixStory extends HTMLElement {
    constructor() {
      super();
      this.bound = false;
      this.motionTimers = new Map();
      this.onResize = () => this.cancelIngredientTransition();
      this.onMotionPreferenceChange = () => {
        if (this.motionPreference?.matches) this.cancelIngredientTransition();
      };
      this.onExampleClick = (event) => {
        const origin = event.target?.closest ? event.target : event.target?.parentElement;
        const button = origin?.closest('button[data-mix-example]');
        if (button) {
          if (!button.disabled && this.showExample(button)) event.preventDefault();
          return;
        }
        const motionButton = origin?.closest('button[data-mix-motion]');
        if (motionButton && !motionButton.disabled && this.playMotion(motionButton)) event.preventDefault();
      };
      this.onBlockSelect = (event) => {
        const id = event.detail?.blockId;
        if (typeof id !== 'string' || !id.trim()) return;
        const buttons = this.ownedElements('button[data-mix-example]');
        let button = buttons.find((element) => element.getAttribute('data-mix-example') === id);
        if (!button) {
          const ingredient = this.ownedElements('[data-mix-ingredient-block]')
            .find((element) => element.getAttribute('data-mix-ingredient-block') === id);
          const category = ingredient?.getAttribute('data-mix-ingredient-category');
          if (category !== 'candy' && category !== 'muesli') return;
          button = buttons.find((element) => element.getAttribute('data-mix-category') === category);
        }
        if (button && !button.disabled) this.showExample(button);
      };
    }

    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.listeningDocument = this.ownerDocument;
      this.listeningWindow = this.listeningDocument.defaultView;
      this.motionPreference = this.listeningWindow?.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.motionPreference?.addEventListener('change', this.onMotionPreferenceChange);
      this.listeningWindow?.addEventListener('resize', this.onResize);
      this.addEventListener('click', this.onExampleClick);
      this.listeningDocument.addEventListener('shopify:block:select', this.onBlockSelect);
      const selected = this.ownedElements('button[data-mix-example]')
        .find((button) => button.getAttribute('aria-pressed') === 'true');
      if (selected) this.showExample(selected, false);
      const viewport = this.ownedElements('[data-mix-ingredients-viewport]')[0];
      if (viewport && this.listeningWindow?.ResizeObserver) {
        let width = viewport.getBoundingClientRect().width;
        this.ingredientObserver = new this.listeningWindow.ResizeObserver(() => {
          const nextWidth = viewport.getBoundingClientRect().width;
          if (nextWidth !== width) this.cancelIngredientTransition();
          width = nextWidth;
        });
        this.ingredientObserver.observe(viewport);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onExampleClick);
      this.listeningDocument?.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.listeningWindow?.removeEventListener('resize', this.onResize);
      this.motionPreference?.removeEventListener('change', this.onMotionPreferenceChange);
      this.ingredientObserver?.disconnect();
      this.ingredientObserver = null;
      this.cancelIngredientTransition();
      this.listeningWindow = null;
      this.motionPreference = null;
      this.listeningDocument = null;
      this.bound = false;
      this.motionTimers.forEach((timer, button) => {
        clearTimeout(timer);
        button.classList.remove('is-motion-active');
      });
      this.motionTimers.clear();
    }

    ownedElements(selector) {
      return Array.from(this.querySelectorAll(selector))
        .filter((element) => element.closest('ss-original-mix-story') === this);
    }

    playMotion(button) {
      if (button.closest('ss-original-mix-story') !== this || this.getAttribute('data-motion') !== 'true') return false;
      if (this.motionTimers.has(button)) {
        clearTimeout(this.motionTimers.get(button));
        button.classList.remove('is-motion-active');
        // Restart this decorative CSS animation on a repeated activation.
        void button.offsetWidth;
      }
      button.classList.add('is-motion-active');
      this.motionTimers.set(button, setTimeout(() => {
        button.classList.remove('is-motion-active');
        this.motionTimers.delete(button);
      }, 800));
      return true;
    }

    cancelIngredientTransition() {
      const animation = this.ingredientAnimation;
      this.ingredientAnimation = null;
      animation?.cancel();
      this.ingredientViewport?.classList.remove('is-changing');
      this.ingredientViewport = null;
    }

    showIngredients(category, animate) {
      const groups = this.ownedElements('[data-mix-ingredients]');
      const changed = groups.some((group) => group.hidden !== (group.getAttribute('data-mix-ingredients') !== category));
      if (!changed) return;
      const viewport = this.ownedElements('[data-mix-ingredients-viewport]')[0];
      // Capture the current interpolated height before cancelling a rapid previous switch.
      const fromHeight = viewport?.getBoundingClientRect().height;
      this.cancelIngredientTransition();
      groups.forEach((group) => { group.hidden = group.getAttribute('data-mix-ingredients') !== category; });
      if (!viewport || !animate || this.getAttribute('data-motion') !== 'true' || this.motionPreference?.matches || !viewport.animate) return;
      const toHeight = viewport.getBoundingClientRect().height;
      if (!fromHeight || !toHeight || fromHeight === toHeight) return;
      this.ingredientViewport = viewport;
      viewport.classList.add('is-changing');
      const animation = viewport.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], {
        duration: 260, easing: 'cubic-bezier(.2,.7,.3,1)'
      });
      this.ingredientAnimation = animation;
      animation.onfinish = () => {
        if (this.ingredientAnimation === animation) this.cancelIngredientTransition();
      };
    }

    showExample(button, announce = true) {
      if (button.closest('ss-original-mix-story') !== this) return false;
      const id = button.getAttribute('data-mix-example');
      if (!id || !id.trim()) return false;
      const panels = this.ownedElements('[data-mix-result]');
      const selected = panels.find((panel) => panel.getAttribute('data-mix-result') === id);
      if (!selected) return false;

      this.ownedElements('button[data-mix-example]').forEach((element) => {
        element.setAttribute('aria-pressed', element === button ? 'true' : 'false');
      });
      panels.forEach((panel) => { panel.hidden = panel !== selected; });
      const category = button.getAttribute('data-mix-category') === 'muesli' ? 'muesli' : 'candy';
      this.showIngredients(category, announce);
      const status = this.ownedElements('[data-mix-status]')[0];
      if (announce && status) status.textContent = button.dataset.mixAnnouncement || '';
      // Focus stays on the activating control; native button keyboard/touch behavior is retained.
      return true;
    }
  }

  customElements.define('ss-original-mix-story', SSOriginalMixStory);
})();
